import { ApiChartRank, ApiProduct, ApiSimpleComponent } from "@incendium/api";
import { DATA_INC_VAR_ATTR } from "../components/LightningLanderVariableMapping";

/** Map product to variable values for data-inc-var slots */
function productToVarValues(product: ApiProduct): Record<string, string> {
  return {
    product_name: product.name ?? "",
    product_price: product.displayPrice ?? (product.price != null ? String(product.price) : ""),
    product_image: product.imageUrl ?? "",
  };
}

/**
 * Fill a single block HTML template with product data.
 * Finds all [data-inc-var] elements and sets text content or img src.
 */
export function fillDynamicBlockTemplate(
  templateHtml: string,
  product: ApiProduct
): string {
  const values = productToVarValues(product);
  const wrap = typeof document !== "undefined" ? document.createElement("div") : null;
  if (!wrap) return templateHtml;

  wrap.innerHTML = templateHtml.replace(/<\/?(?:html|head|body)[^>]*>/gi, "").trim();

  const elements = wrap.querySelectorAll(`[${DATA_INC_VAR_ATTR}]`);
  elements.forEach((el) => {
    const varName = el.getAttribute(DATA_INC_VAR_ATTR);
    if (!varName) return;
    const value = values[varName] ?? "";
    if (el.tagName === "IMG") {
      el.setAttribute("src", value);
    } else {
      el.textContent = value;
    }
  });

  return wrap.innerHTML;
}

const GRID_WRAPPER_CLASS = "inc-dynamic-product-grid";
const GRID_WRAPPER_ATTR = "data-inc-dynamic-grid";

/** Stored on the section so we can reopen the dialog for editing */
export const GRID_CONFIG_ATTR = "data-inc-dynamic-config";

export type ProductGridConfigSource =
  | "manual"
  | "custom"
  | "top_sellers"
  | "best_converting_products"
  | "highly_clicked_products"
  | "cart_magnets"
  | "hidden_gems"
  | "popular_with_returning_customers"
  | "new_customer_favourites";

export interface ProductGridConfig {
  blockId: number;
  productIds: number[];
  productCount: number;
  columnsDesktop: number;
  /** Persisted so edits reuse the same grid class (no new CSS, safe with multiple grids) */
  uniqueId?: string;
  /** When grid was built from analytics top-N; used to pre-fill edit dialog */
  productSource?: ProductGridConfigSource;
  /** Number of products (top N) when productSource is analytics strategy */
  topN?: number;
  /** Date range in days (e.g. 30, 60, 90, 120, 360) when productSource is top_* */
  lastNDays?: number;
  /** Used when productSource is custom */
  customRanks?: ApiChartRank[];
}

/** Generate a unique class suffix so each grid has its own scoped CSS (settings don't override each other) */
function getUniqueGridId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 12);
  }
  return `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/** Desktop breakpoint (mobile is 1 column below this) */
const DESKTOP_MIN_WIDTH = "768px";

/** Regex for #id in selector context (valid CSS id: letter/underscore/hyphen then alphanumeric/_-) */
const ID_SELECTOR_REGEX = /#([a-zA-Z_][a-zA-Z0-9_-]*)/g;

/**
 * Strip rules that only target body or *; we only care about component-scoped styles.
 */
function stripBodyAndUniversalRules(blockCss: string): string {
  if (!blockCss.trim()) return blockCss;
  return processCssRules(blockCss, (selector) => {
    const trimmed = selector.trim();
    if (!trimmed) return null;
    const isBodyOrUniversal =
      trimmed === "body" ||
      trimmed === "*" ||
      /^body\s+/.test(trimmed) ||
      /^\*\s+/.test(trimmed) ||
      /^body,/.test(trimmed) ||
      /^\*,/.test(trimmed);
    return isBodyOrUniversal ? null : selector;
  });
}

/**
 * Rewrite block CSS so #id selectors also match repeated elements (id-2, id-3, ...)
 * that GrapesJS assigns when the same HTML is repeated in the grid.
 * Output format: #id .class, [id^="id-"] .class { ... } so both the original and
 * repeated ids match. Only replaces #id in selector context (not hex colors in values).
 */
function scopeBlockCssForRepeatedGrid(blockCss: string): string {
  if (!blockCss.trim()) return blockCss;
  const withoutBodyStar = stripBodyAndUniversalRules(blockCss);
  return processCssRules(withoutBodyStar, (selector) => {
    ID_SELECTOR_REGEX.lastIndex = 0;
    if (!ID_SELECTOR_REGEX.test(selector)) return selector;
    ID_SELECTOR_REGEX.lastIndex = 0;
    const duplicated = selector.replace(
      ID_SELECTOR_REGEX,
      (_m, id: string) => `[id^="${id}-"]`
    );
    return `${selector}, ${duplicated}`;
  });
}

/**
 * Walk CSS and run fn(selector) for each rule; fn returns replacement selector or null to drop rule.
 * Handles top-level rules and @media blocks (processes inner rules recursively).
 */
function processCssRules(
  css: string,
  fn: (selector: string) => string | null
): string {
  const out: string[] = [];
  let i = 0;
  const skipWhitespace = () => {
    while (i < css.length && /[\s\n\r\t]/.test(css[i])) i++;
  };
  const findMatchingBrace = (start: number): number => {
    let depth = 1;
    let j = start + 1;
    while (j < css.length && depth) {
      if (css[j] === "{") depth++;
      else if (css[j] === "}") depth--;
      j++;
    }
    return depth === 0 ? j - 1 : -1;
  };

  while (i < css.length) {
    skipWhitespace();
    if (i >= css.length) break;
    if (css[i] === "}") {
      i++;
      continue;
    }
    if (css[i] === "@") {
      const atStart = i;
      while (i < css.length && css[i] !== "{") i++;
      if (i >= css.length) break;
      const braceStart = i;
      const braceEnd = findMatchingBrace(braceStart);
      if (braceEnd < 0) break;
      const atRuleHeader = css.slice(atStart, braceStart);
      const inner = css.slice(braceStart + 1, braceEnd);
      out.push(atRuleHeader, "{\n", processCssRules(inner, fn), "\n}");
      i = braceEnd + 1;
      continue;
    }
    const selStart = i;
    while (i < css.length && css[i] !== "{") i++;
    if (i >= css.length) break;
    const selector = css.slice(selStart, i).trim();
    const braceStart = i;
    const braceEnd = findMatchingBrace(braceStart);
    if (braceEnd < 0) break;
    const block = css.slice(braceStart, braceEnd + 1);
    const replacement = fn(selector);
    if (replacement !== null) {
      out.push(replacement, " ", block, "\n");
    }
    i = braceEnd + 1;
  }
  return out.join("");
}

export interface ProductGridOptions {
  /** Max number of products to show (takes first N from list) */
  maxProducts?: number;
  /** Number of columns on desktop; mobile is always 1 */
  columnsDesktop?: number;
  /** Stored in HTML for edit; when provided, section gets GRID_CONFIG_ATTR */
  config?: ProductGridConfig;
}

function buildGridLayoutCss(
  columnsDesktop: number,
  uniqueClass: string
): string {
  return `
.${uniqueClass} {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  padding: 1rem 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.${uniqueClass} > * {
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}
@media (min-width: ${DESKTOP_MIN_WIDTH}) {
  .${uniqueClass} {
    grid-template-columns: repeat(${columnsDesktop}, minmax(0, 1fr));
  }
}
`;
}

export interface ProductGridResult {
  html: string;
  css: string;
}

/**
 * Build full product grid HTML and CSS from a block template and product list.
 */
export function buildProductGridHtml(
  block: ApiSimpleComponent,
  products: ApiProduct[],
  options: ProductGridOptions = {}
): ProductGridResult {
  const { maxProducts, columnsDesktop = 4, config } = options;
  const templateHtml = block.html ?? "";
  const blockCss = block.css ?? "";

  const capped =
    maxProducts != null && maxProducts > 0
      ? products.slice(0, maxProducts)
      : products;

  const itemHtmls = capped.map((product) =>
    fillDynamicBlockTemplate(templateHtml, product)
  );

  const innerHtml = itemHtmls.join("");
  const uniqueId = config?.uniqueId ?? getUniqueGridId();
  const uniqueClass = `${GRID_WRAPPER_CLASS}-${uniqueId}`;
  const configToStore =
    config != null ? { ...config, uniqueId } : undefined;
  const configAttr =
    configToStore != null
      ? ` ${GRID_CONFIG_ATTR}="${encodeURIComponent(JSON.stringify(configToStore))}"`
      : "";
  const wrapperHtml = `<section class="${GRID_WRAPPER_CLASS} ${uniqueClass}" ${GRID_WRAPPER_ATTR}="product"${configAttr}>${innerHtml}</section>`;

  const gridCss = buildGridLayoutCss(
    Math.min(6, Math.max(1, columnsDesktop ?? 4)),
    uniqueClass
  );
  const scopedBlockCss = scopeBlockCssForRepeatedGrid(blockCss);
  const css = scopedBlockCss.trim()
    ? `${gridCss}\n${scopedBlockCss}`
    : gridCss;

  return { html: wrapperHtml, css };
}
